import React, { useCallback, useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { NPaymentProviderDto } from '@/api/NPayments/Types/NPaymentProviderDto'
import { Pager } from '@/components/components/Pager'
import { FilterType } from '@/components/Filter'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import { ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { useGridState } from '@/features/DataGrid'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from '@/store/grid'
import { fetchFilterAction } from '@/store/identity/identity.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { NPaymentsApi } from '@/utils/api/nPaymentsApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import { buildQuery } from '@/utils/QueryHelpers'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { useApiClient } from 'utils/api/ApiClient'
import { links } from 'utils/featureToggles'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import MoreButton from '../../../components/MoreButton/MoreButton'
import { getProviderConfigFilterOptions, paymentProviderColumns } from './paymentProviderColumns'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
})
const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}
const PaymentProvidersConfigPage = (): JSX.Element => {
  const nPaymentsApi = useApiClient(NPaymentsApi)
  const crumbs = [
    { title: 'Payment Providers Config', link: links.administrationPaymentProvidersConfig.url },
  ]
  const { gridState, gridActions } = useGridState()
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [data, setData] = useState<NPaymentProviderDto[]>()
  const { filters } = useMapStateToProps()
  const { fetchFilters, resetGridFilters } = useMapDispatchToProps()
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const permissions = auth?.permissions || []

  useEffect(() => {
    if (!gridState.filter?.filters?.length && gridState.pageIndex === 1) {
      resetGridFilters()
      if (isEmpty(filters)) {
        fetchFilters()
      }
    }
  }, [])

  const updatePspConfig = useCallback(async (config: NPaymentProviderDto, enabled: boolean) => {
    await nPaymentsApi.updatePaymentProviderDetailConfig({ ...config, enabled })
    setToast({ type: 'success', title: 'Config has been updated.' })
    await fetchConfig()
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [
    gridState.pageSize,
    gridState.pageIndex,
    gridState.take,
    // tableData?.skipCount,
    // gridState.sort.length,
  ])

  const fetchConfig = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = gridState.skip ?? 0
      const response = await nPaymentsApi.getPaymentProvidersConfig({
        search,
        pageIndex,
        pageSize,
      })

      if (response?.items) {
        setData(response.items)
      } else {
        setData([])
      }

      gridActions.setGridFilters({ take, skip, total: response.totalResults, pageIndex })
      setTableData({ ...tableData, meta: response })
    },
    [gridState, tableData]
  )

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (!gridState.filter) {
        return null
      }
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    gridActions.setGridFilters({ ...gridState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    gridActions.setGridFilters({ pageIndex })

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={fetchConfig}
        filterType={FilterType.PaymentProviderConfig}
        gridState={gridState}
        filterFields={getProviderConfigFilterOptions()}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={data}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
        >
          {paymentProviderColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
          <Column
            width={40}
            title=''
            filterCell={() => null}
            cell={(props) => (
              <td>
                <MoreButton
                  disabled={!hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)}
                  items={[
                    {
                      name: 'Enable',
                      'data-test': 'psp-config-enable',
                      disabled: (props.dataItem as NPaymentProviderDto).enabled,
                      onClick: () => updatePspConfig(props.dataItem as NPaymentProviderDto, true),
                    },
                    {
                      name: 'Disable',
                      'data-test': 'psp-config-disable',
                      disabled: !(props.dataItem as NPaymentProviderDto).enabled,
                      onClick: () => updatePspConfig(props.dataItem as NPaymentProviderDto, false),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default PaymentProvidersConfigPage
